import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../cn';
import { CheckIcon, ChevronDownIcon, SpinnerIcon } from '../icons';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { fieldClassName } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface ComboboxOption {
  value: string;
  label: string;
  /** Secondary line, e.g. masked Emirates ID or bank code. */
  description?: ReactNode;
  disabled?: boolean;
}

export interface ComboboxProps {
  id?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: readonly ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  /** Server-side search: when provided, filtering is not done locally. */
  onSearchChange?: (query: string) => void;
  loading?: boolean;
  disabled?: boolean;
  /** Extra content at the bottom of the list, e.g. "New customer". */
  footer?: ReactNode;
  className?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-label'?: string;
  /** Label shown when the selected value is not in the current options (async lists). */
  selectedLabel?: string;
}

/** Searchable single select. Keyboard: type to filter, ↑/↓ to move, Enter to choose. */
export function Combobox({
  id,
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  onSearchChange,
  loading,
  disabled,
  footer,
  className,
  selectedLabel,
  ...aria
}: ComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  const display = selected?.label ?? selectedLabel;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          // eslint-disable-next-line jsx-a11y/role-has-required-aria-props -- Radix PopoverTrigger adds aria-controls at runtime.
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          aria-invalid={aria['aria-invalid']}
          aria-describedby={aria['aria-describedby']}
          aria-label={aria['aria-label']}
          className={cn(fieldClassName, 'flex h-8 items-center justify-between gap-2 px-2.5 text-start', !display && 'text-ink-muted', className)}
        >
          <span className="truncate">{display ?? placeholder ?? t('form.selectPlaceholder')}</span>
          <ChevronDownIcon size={14} className="text-ink-muted" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) min-w-64 p-0">
        <Command shouldFilter={!onSearchChange} loop>
          <CommandInput placeholder={searchPlaceholder ?? t('form.searchPlaceholder')} onValueChange={onSearchChange} />
          <CommandList>
            {loading ? (
              <div className="flex items-center gap-2 px-3 py-3 text-body text-ink-muted">
                <SpinnerIcon size={14} className="animate-spin" />
              </div>
            ) : (
              <CommandEmpty>{t('form.noOptions')}</CommandEmpty>
            )}
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${o.value}`}
                  disabled={o.disabled}
                  onSelect={() => {
                    onChange(o.value === value ? null : o.value);
                    setOpen(false);
                  }}
                >
                  <CheckIcon size={14} className={cn('text-accent', o.value === value ? 'opacity-100' : 'opacity-0')} />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{o.label}</span>
                    {o.description ? <span className="truncate text-meta text-ink-muted">{o.description}</span> : null}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {footer ? <div className="border-t border-border p-1">{footer}</div> : null}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
